import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Effect, Actions, ofType } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import { Action, select, Store } from '@ngrx/store';
import {
  switchMap,
  map,
  catchError,
  filter,
  pluck,
  tap,
  withLatestFrom,
  ignoreElements, distinctUntilChanged,
} from 'rxjs/operators';

import {
  CustomPlpActionTypes,
  LoadCustomPlpConfigContent,
  LoadCustomPlpConfigContentSuccess,
  LoadCustomPlpConfigContentFailure,
} from './custom-plp.actions';
import * as fromRouter from '~core/store';
import { CatalogService } from '../../services/catalog.service';
import { CustomPlpState } from './custom-plp.state';
import { ErrorResponse } from '~shared/models';
import { APP_CUSTOM_PLP_ROUTE } from '~shared/constants';
import { UrlUtils } from '~app/modules/generic/utils/url.utils';
import { NavigationHelper } from '~shared/services/navigation-helper.service';
import { CustomPlpService } from '~app/modules/catalog/services/custom-plp.service';
import { RouterNavigatedAction, ROUTER_NAVIGATED } from '@ngrx/router-store';

@Injectable({
  providedIn: 'root'
})
export class CustomPlpEffects {
  private activeRoute$ = this.store.pipe(select(fromRouter.getRouterState));

  constructor(
    private actions$: Actions,
    private catalogService: CatalogService,
    private store: Store<fromRouter.RouterState>,
    private activatedRoute: ActivatedRoute,
    private navigationHelper: NavigationHelper,
    private customPLPService: CustomPlpService,
  ) {
  }

  @Effect()
  loadCustomPlpConfigContent$: Observable<Action> = this.actions$.pipe(
    ofType(CustomPlpActionTypes.LOAD_CUSTOMPLP_CONFIG_CONTENT),
    switchMap((action: LoadCustomPlpConfigContent) =>
      this.catalogService.getCustomPlpConfig(action.payload).pipe(
        tap((response: CustomPlpState) =>
          this.customPLPService.setSortedIds(response)
        ),
        map((response: CustomPlpState) =>
            new LoadCustomPlpConfigContentSuccess({ response })
        ),
        catchError((error: ErrorResponse) =>
          of(new LoadCustomPlpConfigContentFailure({ error }))
        )
      )
    )
  );

  @Effect()
  loadCustomPlpConfigContentByRoute$: Observable<Action> = this.actions$.pipe(
    ofType(ROUTER_NAVIGATED),
    map((action: RouterNavigatedAction) => action.payload.routerState),
    filter((routerState: any) => routerState && UrlUtils.isCustomPLPPage(routerState.url)),
    map((routerState) => routerState.params.slug),
    distinctUntilChanged(),
    switchMap((slug: string) => of(new LoadCustomPlpConfigContent(slug)))
  );

  @Effect()
  updateRouteBasedOnCustomPlpConfig$: Observable<void> = this.actions$.pipe(
    ofType(CustomPlpActionTypes.LOAD_CUSTOMPLP_CONFIG_CONTENT_SUCCESS),
    withLatestFrom(this.activeRoute$),
    filter(([state, routerState]) => {
      return this.checkFiltersToBeApplied(state, routerState);
    }),
    map(([action]) => action),
    pluck('payload', 'response'),
    tap((state: CustomPlpState) => {
      this.navigationHelper.replaceUrl([`${APP_CUSTOM_PLP_ROUTE}/${state.slug}`], {
        relativeTo: this.activatedRoute,
        queryParams: this.customPLPService.buildQueryParams(state),
        queryParamsHandling: 'merge'
      });
    }),
    ignoreElements()
  );

  private checkFiltersToBeApplied(state, routerState) {
    const responseFilter = state?.payload?.response?.filters;
    const queryParamsFilter = routerState?.queryParams?.filter;

    if (Object.keys(state).length === 0 || Object.keys(responseFilter).length === 0) {
      return false;
    }

    /*
    * This is for checking if custom PLP API response has isHasPromotion filter and not promoTag filter
    * and the url queryParams also has promotion filter.
    * Eg: API response filters:{isHasPromotion: true, promoTag: null}
    * Eg: custom plp url should be /landing/{slug}?f.promotion=true
    * & queryParams filter is filter: {promotion: true}
    * */
    const isPromotionFilterAvailable =
      responseFilter?.isHasPromotion && !queryParamsFilter?.promoTag && queryParamsFilter?.promotion;

    /*
    * This is for checking if custom PLP API response has promoTag filter and not isHasPromotion filter
    * and the url queryParams also has promoTag filter.
    * Eg: API response filters:{isHasPromotion: null, promoTag: 'promo-plp'}
    * Eg: custom plp url should be /landing/{slug}?f.promoTag=promo-plp
    * & queryParams filter is filter: {promoTag: 'promo-plp'}
    * */
    const isPromoTagFilterAvailable =
      responseFilter?.promoTag && !responseFilter?.isHasPromotion && queryParamsFilter?.promoTag;
    /*
    * This is for checking if custom PLP API response has promoTag filter as well as isHasPromotion filter
    * and the url queryParams also has both filters.
    * Eg: API response filters:{isHasPromotion: true, promoTag: 'promo-plp'}
    * Eg: custom plp url should be /landing/{slug}?f.promoTag=promo-plp&f.promotion=true
    * & queryParams filter is filter: {promoTag: 'promo-plp', promotion: true}
    * */
    const isBothFiltersAvailable = responseFilter?.promoTag && responseFilter?.isHasPromotion
      && queryParamsFilter?.promotion && queryParamsFilter?.promoTag;

    if (isPromotionFilterAvailable || isPromoTagFilterAvailable || isBothFiltersAvailable) {
      return false;
    }
    return true;
  }
}
